/**
 * THE GRANT LEDGER — the running totals of what codes and admins have granted onto a save (server/redeem.js), as
 * the game carries them. Its own module, import-free, because sim/save.ts needs it and sim/redeem.ts imports
 * sim/save.ts. The story is in sim/redeem.ts's header.
 */

export interface GrantLedger {
  coins: number;
  seeds: Record<string, number>;
  decor: Record<string, number>;
  achievements: string[];
  /** COSMETICS granted directly ("pot:voidAnodized", "name:iridescent" — sim/backerCosmetics.ts), a set like
   *  `achievements`. Unlike the other keys this IS the record of ownership: nothing else in the save holds them. */
  cosmetics: string[];
}

export const EMPTY_LEDGER: GrantLedger = { coins: 0, seeds: {}, decor: {}, achievements: [], cosmetics: [] };

function countMap(v: unknown): Record<string, number> {
  const out: Record<string, number> = {};
  if (!v || typeof v !== "object" || Array.isArray(v)) return out;
  for (const [k, n] of Object.entries(v as Record<string, unknown>)) {
    if (typeof n === "number" && Number.isFinite(n) && n > 0) out[k] = Math.floor(n);
  }
  return out;
}

const idSet = (v: unknown): string[] => (Array.isArray(v) ? [...new Set(v.filter((k): k is string => typeof k === "string"))] : []);

/** Coerce a stored / received ledger (absent or junk ⇒ empty). Mirrors server/redeem.js `coerceGrantLedger`. */
export function coerceGrantLedger(v: unknown): GrantLedger {
  const o = (v && typeof v === "object" && !Array.isArray(v) ? v : {}) as Record<string, unknown>;
  const coins = typeof o.coins === "number" && Number.isFinite(o.coins) && o.coins > 0 ? Math.floor(o.coins) : 0;
  return {
    coins,
    seeds: countMap(o.seeds),
    decor: countMap(o.decor),
    achievements: idSet(o.achievements),
    cosmetics: idSet(o.cosmetics),
  };
}

/** What `next` adds over the acknowledged `acked`: per-key positive differences, plus new achievement and cosmetic ids. */
export function ledgerDelta(next: GrantLedger, acked: GrantLedger): GrantLedger {
  const diff = (n: Record<string, number>, a: Record<string, number>) => {
    const out: Record<string, number> = {};
    for (const [k, v] of Object.entries(n)) { const d = v - (a[k] ?? 0); if (d > 0) out[k] = d; }
    return out;
  };
  return {
    coins: Math.max(0, next.coins - acked.coins),
    seeds: diff(next.seeds, acked.seeds),
    decor: diff(next.decor, acked.decor),
    achievements: next.achievements.filter((id) => !acked.achievements.includes(id)),
    cosmetics: next.cosmetics.filter((k) => !acked.cosmetics.includes(k)),
  };
}

/** Does a delta carry anything? */
export function ledgerHasAny(d: GrantLedger): boolean {
  return d.coins > 0 || Object.keys(d.seeds).length > 0 || Object.keys(d.decor).length > 0 || d.achievements.length > 0 || d.cosmetics.length > 0;
}

/** The larger of two acknowledgements, key by key — an older message arriving late must never lower one. */
export function ledgerMax(a: GrantLedger, b: GrantLedger): GrantLedger {
  const mx = (x: Record<string, number>, y: Record<string, number>) => {
    const out = { ...x };
    for (const [k, v] of Object.entries(y)) out[k] = Math.max(out[k] ?? 0, v);
    return out;
  };
  return {
    coins: Math.max(a.coins, b.coins),
    seeds: mx(a.seeds, b.seeds),
    decor: mx(a.decor, b.decor),
    achievements: [...new Set([...a.achievements, ...b.achievements])],
    cosmetics: [...new Set([...a.cosmetics, ...b.cosmetics])],
  };
}

/** `counts` with `adds` added (a new object). */
export function addCounts(counts: Record<string, number>, adds: Record<string, number>): Record<string, number> {
  const out = { ...counts };
  for (const [k, n] of Object.entries(adds)) out[k] = (out[k] ?? 0) + n;
  return out;
}

